// src/pages/NewsPost.jsx
import React from 'react';
import styled from 'styled-components';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { normalizeMediaUrl } from '../api/base';
import RelatedPostsSlider from '../components/RelatedPostsSlider';
import QuickConsult from '../components/QuickConsult';
import ReadingProgress from '../components/ReadingProgress';

const Wrap = styled.main`
  padding: 0;
  min-height: 100vh;
  background: var(--bg);
`;

const Container = styled.div`
  width: min(1400px, 96vw);
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0 16px;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 40px;
  margin-top: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const Header = styled.header`
  padding: 20px 0;
  border-bottom: 1px solid var(--bdr);
`;

const Breadcrumb = styled.nav`
  margin-bottom: 20px;
  font-size: 14px;
  color: var(--muted);
  
  a {
    color: var(--muted);
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: var(--primary);
    }
  }
  
  span {
    margin: 0 8px;
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  margin: 0 0 16px 0;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.2;
  font-weight: 700;
  color: var(--fg);
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  color: var(--muted);
  font-size: 14px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UpdatedDate = styled.span`
  color: var(--muted);
  font-size: 13px;
`;

const ArticleContent = styled.article`
  background: var(--card);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow);
`;

const CoverSection = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    cursor: zoom-in;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
    }
  }
`;

const ContentSection = styled.div`
  padding: 30px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Body = styled.div`
  color: var(--fg);
  font-size: 16px;
  line-height: 1.7;
  white-space: pre-wrap; /* Сохраняет переносы строк и пробелы */
  
  p {
    margin: 0 0 20px;
    white-space: normal; /* Для параграфов используем обычное поведение */
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  h2 {
    font-size: 24px;
    font-weight: 600;
    margin: 30px 0 16px;
    color: var(--fg);
    line-height: 1.3;
  }
  
  h3 {
    font-size: 20px;
    font-weight: 600;
    margin: 24px 0 12px;
    color: var(--fg);
    line-height: 1.4;
  }
  
  ul, ol {
    margin: 0 0 20px;
    padding-left: 20px;
    
    li {
      margin-bottom: 6px;
      line-height: 1.6;
    }
  }
  
  blockquote {
    background: rgba(176, 141, 87, 0.1);
    border-left: 4px solid var(--primary);
    padding: 16px 20px;
    margin: 24px 0;
    border-radius: 6px;
    font-style: italic;
    
    p {
      margin: 0;
      font-size: 16px;
      color: var(--fg);
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 24px auto;
    border-radius: 8px;
    cursor: zoom-in;
    box-shadow: var(--shadow);
  }
  
  strong {
    font-weight: 600;
    color: var(--fg);
  }
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Sidebar = styled.aside`
  @media (max-width: 1024px) {
    order: -1;
  }
`;

const SidebarSection = styled.div`
  background: var(--card);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow);
`;

const SidebarTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--fg);
`;

const RecentPost = styled(Link)`
  display: block;
  padding: 12px 0;
  border-bottom: 1px solid var(--bdr);
  text-decoration: none;
  color: inherit;
  transition: color 0.2s ease;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:hover {
    color: var(--primary);
  }
`;

const RecentPostDate = styled.div`
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 4px;
`;

const RecentPostTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const TagsSection = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--bdr);
`;

const TagsTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ConsultationSection = styled.section`
  background: var(--bg);
  padding: 40px 0;
  border-top: 1px solid var(--bdr);
  
  @media (max-width: 768px) {
    padding: 30px 0;
  }
`;

const ConsultationContainer = styled.div`
  width: min(800px, 92vw);
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const ConsultationTitle = styled.h3`
  margin: 0 0 24px 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--fg);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
  }
`;

const ConsultationSubtitle = styled.p`
  margin: 0 0 30px 0;
  font-size: 16px;
  color: var(--muted);
  text-align: center;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 24px;
  }
`;

const Tag = styled.span`
  background: var(--bg);
  color: var(--muted);
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--bdr);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--primary);
    color: var(--primaryFg);
    border-color: var(--primary);
  }
`;

/* Lightbox */
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.95);
  display: grid;
  place-items: center;
  backdrop-filter: blur(4px);
`;

const LBInner = styled.div`
  width: min(1200px, 96vw);
  height: min(92vh, 900px);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LBImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const LBClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }
`;

const LBNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: 20px;
  }
  
  &.next {
    right: 20px;
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    
    &:hover {
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.7);
      border-color: rgba(255, 255, 255, 0.3);
    }
  }
`;

const LBCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

// Нормализуем URLы: убираем thumb_ и переносим на PUBLIC_ORIGIN
function normalizeFull(url) {
  if (!url) return '';
  const fixed = String(url).replace('/uploads/thumb_', '/uploads/');
  return normalizeMediaUrl(fixed);
}

function rewriteUploadsInHtml(html) {
  if (!html) return '';
  return html
    .replace(/src\s*=\s*"(.*?)"/g, (_m, p1) => `src="${normalizeFull(p1)}"`)
    .replace(/src\s*=\s*'(.*?)'/g, (_m, p1) => `src='${normalizeFull(p1)}'`);
}

// Функция для расчета времени чтения (средняя скорость 200 слов в минуту)
function calculateReadingTime(html) {
  if (!html) return 1;
  
  // Убираем HTML теги и считаем слова
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const wordCount = text.split(' ').filter(word => word.length > 0).length;
  
  // Средняя скорость чтения 200 слов в минуту
  const readingTime = Math.ceil(wordCount / 200);
  
  return Math.max(1, readingTime); // Минимум 1 минута
}

export default function NewsPost() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [post, setPost] = React.useState(null);
  const [relatedPosts, setRelatedPosts] = React.useState([]);
  const [err, setErr] = React.useState('');

  const [lbOpen, setLbOpen] = React.useState(false);
  const [lbImages, setLbImages] = React.useState([]);
  const [lbIndex, setLbIndex] = React.useState(0);

  const bodyRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setErr('');
      try {
        const [postData, relatedData] = await Promise.all([
          PublicAPI.newsPost(slug, { locale: i18n.language }),
          PublicAPI.newsPosts({ page_size: 6, locale: i18n.language }) // Загружаем больше для выбора
        ]);
        
        if (!postData) throw new Error('Not found');
        
        if (!cancelled) {
          setPost(postData);
          // Устанавливаем связанные посты, исключая текущий
          const related = Array.isArray(relatedData) ? relatedData : relatedData?.items || [];
          setRelatedPosts(related);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || 'Error');
          setTimeout(() => navigate('/news', { replace: true }), 0);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [slug, i18n.language, navigate]);

  // Подключаем клики к картинкам внутри body
  React.useEffect(() => {
    if (!post) return;
    const el = bodyRef.current;
    if (!el) return;

    const imgs = Array.from(el.querySelectorAll('img')).filter(Boolean);
    const srcs = imgs.map(img => img.getAttribute('src')).filter(Boolean);

    const onClick = (e) => {
      const target = e.target;
      if (target && target.tagName === 'IMG') {
        const src = target.getAttribute('src');
        const idx = srcs.indexOf(src);
        setLbImages(srcs);
        setLbIndex(Math.max(0, idx));
        setLbOpen(true);
      }
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [post]);

  // Добавляем структурированные данные JSON-LD
  React.useEffect(() => {
    if (!post) return;
    
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": post.title,
      "description": post.excerpt || post.title,
      "image": post.cover ? normalizeFull(post.cover) : undefined,
      "datePublished": post.date,
      "dateModified": post.updated_at || post.date,
      "author": {
        "@type": "Organization",
        "name": t('company'),
        "url": window.location.origin
      },
      "publisher": {
        "@type": "Organization",
        "name": t('company'),
        "url": window.location.origin
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      }
    };
    
    // Удаляем undefined значения
    Object.keys(jsonLd).forEach(key => {
      if (jsonLd[key] === undefined) {
        delete jsonLd[key];
      }
    });
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    script.id = 'news-post-jsonld';
    
    // Удаляем предыдущий скрипт, если есть
    const existing = document.getElementById('news-post-jsonld');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('news-post-jsonld');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [post, t]);

  // блокировка скролла + esc/стрелки
  React.useEffect(() => {
    if (!post) return;
    
    // Обновляем title страницы
    document.title = `${post.title} - ${t('company')} ${t('subtitle')}`;
    
    // Мета-теги для SEO
    const updateMetaTag = (name, content, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };
    
    // Базовые мета-теги
    updateMetaTag('description', post.excerpt || post.title);
    updateMetaTag('keywords', post.tag ? `${post.tag}, недвижимость, новости` : 'недвижимость, новости');
    
    // Open Graph для социальных сетей
    updateMetaTag('og:title', post.title, true);
    updateMetaTag('og:description', post.excerpt || post.title, true);
    updateMetaTag('og:type', 'article', true);
    updateMetaTag('og:url', window.location.href, true);
    
    if (post.cover) {
      updateMetaTag('og:image', normalizeFull(post.cover), true);
    }
    
    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', post.title);
    updateMetaTag('twitter:description', post.excerpt || post.title);
    
    if (post.cover) {
      updateMetaTag('twitter:image', normalizeFull(post.cover));
    }
    
    // Очистка при размонтировании
    return () => {
      document.title = `${t('company')} - ${t('subtitle')}`;
    };
  }, [post, t]);
  
  React.useEffect(() => {
    if (!lbOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setLbOpen(false);
      if (e.key === 'ArrowLeft') setLbIndex(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setLbIndex(i => Math.min((lbImages.length || 1) - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [lbOpen, lbImages.length]);

  if (!post) return null;

  const fullCover = normalizeFull(post.cover);
  const bodyHtml = rewriteUploadsInHtml(post.body_html || '');
  
  const openCover = () => {
    if (!fullCover) return;
    setLbImages([fullCover, ...lbImages.filter(src => src !== fullCover)]);
    setLbIndex(0);
    setLbOpen(true);
  };

  return (
    <>
      <ReadingProgress />
      <Wrap>
        <Container>
          <Header>
            <Breadcrumb>
              <Link to="/">{t('nav.home', 'Главная')}</Link>
              <span>/</span>
              <Link to="/news">{t('nav.news', 'Новости')}</Link>
              <span>/</span>
              <span>{post.title}</span>
            </Breadcrumb>
            
            <ArticleHeader>
              <Title>{post.title}</Title>
              <Meta>
                <MetaItem>
                  📅 {new Date(post.date || post.created_at).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </MetaItem>
                {post.updated_at && post.updated_at !== post.date && (
                  <UpdatedDate>
                    / Обновлено: {new Date(post.updated_at).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </UpdatedDate>
                )}
                <MetaItem>
                  ⏱️ читать {calculateReadingTime(post.body_html || post.content || '')} мин
                </MetaItem>
              </Meta>
            </ArticleHeader>
          </Header>

          <MainContent>
            <ArticleContent>
              {fullCover && (
                <CoverSection onClick={openCover} title="Открыть изображение">
                  <img
                    src={fullCover}
                    alt={post.title}
                    loading="eager"
                    decoding="async"
                  />
                </CoverSection>
              )}

              <ContentSection>
                <Body ref={bodyRef} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                {err && <div style={{ color: '#c62828', marginTop: 12 }}>{err}</div>}
                
                {/* Теги статьи */}
                {post.tags && post.tags.length > 0 && (
                  <TagsSection>
                    <TagsTitle>Теги</TagsTitle>
                    <TagsList>
                      {post.tags.map((tag, index) => (
                        <Tag key={index}>{tag}</Tag>
                      ))}
                    </TagsList>
                  </TagsSection>
                )}
              </ContentSection>
            </ArticleContent>

            <Sidebar>
              <SidebarSection>
                <SidebarTitle>Последние новости</SidebarTitle>
                {relatedPosts.slice(0, 5).map((relatedPost) => (
                  <RecentPost key={relatedPost.id} to={`/news/${relatedPost.slug}`}>
                    <RecentPostDate>
                      {new Date(relatedPost.date || relatedPost.created_at).toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </RecentPostDate>
                    <RecentPostTitle>{relatedPost.title}</RecentPostTitle>
                  </RecentPost>
                ))}
                
                {relatedPosts.length === 0 && (
                  <div style={{ color: 'var(--muted)', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
                    Нет доступных новостей
                  </div>
                )}
              </SidebarSection>
            </Sidebar>
          </MainContent>

          {/* Форма быстрой консультации */}
          <ConsultationSection>
            <ConsultationContainer>
              <ConsultationTitle>
                {t('consultation.title', 'Получить консультацию')}
              </ConsultationTitle>
              <ConsultationSubtitle>
                {t('consultation.subtitle', 'Заполните данные и наш специалист свяжется с вами в ближайшее время для уточнения деталей.')}
              </ConsultationSubtitle>
              <QuickConsult />
            </ConsultationContainer>
          </ConsultationSection>
        </Container>

        {/* Слайдер "Читайте также" - в самом конце страницы */}
        <RelatedPostsSlider 
          posts={relatedPosts} 
          currentSlug={slug} 
          maxPosts={6}
          basePath="/news"
        />

        {lbOpen && !!lbImages.length && (
          <Lightbox onClick={() => setLbOpen(false)}>
            <LBInner onClick={(e) => e.stopPropagation()}>
              <LBClose onClick={() => setLbOpen(false)} aria-label={t('newsPost.lightbox.close', 'Закрыть')}>
                ✕
              </LBClose>
              
              {lbImages.length > 1 && (
                <>
                  <LBNav
                    className="prev"
                    onClick={() => setLbIndex(i => Math.max(0, i - 1))}
                    disabled={lbIndex === 0}
                    aria-label={t('newsPost.lightbox.prev', 'Предыдущее изображение')}
                  >
                    ←
                  </LBNav>
                  <LBNav
                    className="next"
                    onClick={() => setLbIndex(i => Math.min(lbImages.length - 1, i + 1))}
                    disabled={lbIndex === lbImages.length - 1}
                    aria-label={t('newsPost.lightbox.next', 'Следующее изображение')}
                  >
                    →
                  </LBNav>
                  <LBCounter>
                    {t('newsPost.lightbox.counter', '{{current}} из {{total}}', { 
                      current: lbIndex + 1, 
                      total: lbImages.length 
                    })}
                  </LBCounter>
                </>
              )}
              
              <LBImg src={lbImages[lbIndex]} alt={`Изображение ${lbIndex + 1}`} />
            </LBInner>
          </Lightbox>
        )}
      </Wrap>
    </>
  );
}
